import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';

// Standard team standup — "yesterday, what was accomplished," one section per teammate. The full-page
// expansion of the daily brief's compact standup block. Backed by GET /api/surfaces/team/standup
// (dual-auth, bare data). Local types mirror the BFF route's JSON (the dominant surface-hook pattern;
// only useAccountSurface parses a Zod contract). Distinct from any real-time "live" standup surface.

export type StandupCategory = 'shipped' | 'still_building' | 'discussed';

export interface StandupItem {
  conversationId: string | null;
  headline: string;
  why: string | null;
  category: StandupCategory;
  site: string | null;
  project: string | null;
  timestamp: string;
}

export interface StandupMember {
  userId: string;
  displayName: string;
  displayRole: string;
  isYou: boolean;
  lastActive: string | null;
  itemCount: number;
  // Plain-words LLM narrative of the member's yesterday (the mock's standup voice). Null when generation
  // failed or the member had no activity — the surface then falls back to rendering `items` directly.
  narrative: string | null;
  items: StandupItem[];
}

export interface TeamStandupData {
  team_name: string | null;
  generated_at: string | null;
  members: StandupMember[];
}

export function useTeamStandupSurface() {
  const query = useQuery({
    queryKey: ['surfaces', 'team-standup'],
    queryFn: () => apiFetch<TeamStandupData>('/api/surfaces/team/standup'),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  return { ...query, data: query.data ?? undefined };
}
